if (!window.__roadRexShellLoaded) {
window.__roadRexShellLoaded = true;

(() => {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const closeSearch = (header, returnFocus = false) => {
    const button = header.querySelector('[data-rr-mobile-search]');
    const search = header.querySelector('.rr-header__search');
    if (!button || !search) return;
    search.classList.remove('is-mobile-open');
    search.removeAttribute('open');
    const input = search.querySelector('input[type="search"]');
    input?.setAttribute('aria-expanded', 'false');
    input?.removeAttribute('aria-activedescendant');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', header.dataset.searchOpenLabel || 'Open product search');
    if (returnFocus) button.focus();
  };

  const closeMenu = (header, returnFocus = false) => {
    const button = header.querySelector('[data-rr-menu-toggle]');
    const menu = header.querySelector('[data-rr-mobile-menu]');
    if (!button || !menu) return;
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', header.dataset.menuOpenLabel || 'Open menu');
    menu.hidden = true;
    document.body.classList.remove('rr-menu-open');
    if (returnFocus) button.focus();
  };

  const initializeHeader = (header) => {
    if (!header || header.dataset.rrInitialized === 'true') return;
    header.dataset.rrInitialized = 'true';

    const syncMobileHeaderOffset = () => {
      const offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--rr-mobile-header-offset', `${offset}px`);
    };

    const menuButton = header.querySelector('[data-rr-menu-toggle]');
    const menu = header.querySelector('[data-rr-mobile-menu]');
    const searchButton = header.querySelector('[data-rr-mobile-search]');
    const search = header.querySelector('.rr-header__search');
    const searchInput = search?.querySelector('input[type="search"]');
    const desktopNavigation = window.matchMedia('(min-width: 990px)');
    const megaDetails = [...header.querySelectorAll('.rr-header-nav-item--mega')];
    const moreDetails = [...header.querySelectorAll('[data-rr-more-nav]')];
    const desktopDetails = [...megaDetails, ...moreDetails];
    const megaProductCache = new Map();
    const megaRequestTokens = new WeakMap();
    const DESKTOP_MENU_OPEN_DELAY = 220;
    const DESKTOP_MENU_CLOSE_DELAY = 180;
    let desktopOpenTimer = null;
    let desktopCloseTimer = null;
    let pendingDesktopDetails = null;
    let keyboardNavigation = false;

    const clearDesktopOpenTimer = () => {
      if (desktopOpenTimer !== null) window.clearTimeout(desktopOpenTimer);
      desktopOpenTimer = null;
      pendingDesktopDetails = null;
    };

    const clearDesktopCloseTimer = () => {
      if (desktopCloseTimer !== null) window.clearTimeout(desktopCloseTimer);
      desktopCloseTimer = null;
    };

    const closeDesktopDetails = (details, returnFocus = false) => {
      if (!details) return;
      details.open = false;
      if (returnFocus) details.querySelector(':scope > summary')?.focus();
    };

    const closeAllDesktopDetails = () => {
      clearDesktopOpenTimer();
      clearDesktopCloseTimer();
      desktopDetails.forEach((details) => closeDesktopDetails(details));
    };

    const openDesktopDetails = (details) => {
      if (!desktopNavigation.matches || !details?.isConnected) return;
      clearDesktopOpenTimer();
      clearDesktopCloseTimer();
      desktopDetails.forEach((sibling) => {
        if (sibling !== details) closeDesktopDetails(sibling);
      });
      details.open = true;
    };

    const scheduleDesktopOpen = (details) => {
      if (!desktopNavigation.matches || !details) return;
      clearDesktopCloseTimer();
      clearDesktopOpenTimer();
      if (details.open) return;
      pendingDesktopDetails = details;
      desktopOpenTimer = window.setTimeout(() => {
        if (pendingDesktopDetails === details) openDesktopDetails(details);
      }, DESKTOP_MENU_OPEN_DELAY);
    };

    const scheduleDesktopClose = () => {
      if (!desktopNavigation.matches) return;
      clearDesktopOpenTimer();
      clearDesktopCloseTimer();
      desktopCloseTimer = window.setTimeout(() => {
        desktopCloseTimer = null;
        const openDetails = desktopDetails.find((details) => details.open);
        if (!openDetails) return;
        if (openDetails.matches(':hover')) return;
        if (keyboardNavigation && openDetails.contains(document.activeElement)) return;
        closeAllDesktopDetails();
      }, DESKTOP_MENU_CLOSE_DELAY);
    };

    const bindDesktopDetailsIntent = (details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !desktopNavigation.matches) return;
        keyboardNavigation = false;
        clearDesktopCloseTimer();
      });
      summary.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        keyboardNavigation = false;
        scheduleDesktopOpen(details);
      });
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        scheduleDesktopClose();
      });
      details.addEventListener('focusin', clearDesktopCloseTimer);
      details.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && details.contains(event.relatedTarget)) return;
        scheduleDesktopClose();
      });
      summary.addEventListener('click', () => {
        clearDesktopOpenTimer();
        clearDesktopCloseTimer();
      });
    };

    desktopDetails.forEach(bindDesktopDetailsIntent);
    header.__rrCloseDesktopNavigation = closeAllDesktopDetails;

    const normalizeStorefrontUrl = (rawUrl) => {
      const url = new URL(rawUrl, window.location.origin);
      if (url.hostname.endsWith('.myshopify.com') || url.hostname === window.location.hostname) {
        return new URL(`${url.pathname}${url.search}${url.hash}`, window.location.origin);
      }
      return url;
    };

    const megaProductEndpoint = (rawUrl, limit) => {
      if (!rawUrl) return null;
      const url = normalizeStorefrontUrl(rawUrl);
      const collectionMatch = url.pathname.match(/^\/collections\/([^/]+)/);
      if (collectionMatch) {
        return {
          endpoint: `${window.Shopify?.routes?.root || '/'}collections/${encodeURIComponent(decodeURIComponent(collectionMatch[1]))}/products.json?limit=${limit}`,
          type: 'collection'
        };
      }
      const productMatch = url.pathname.match(/^\/products\/([^/]+)/);
      if (productMatch) {
        return {
          endpoint: `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(decodeURIComponent(productMatch[1]))}.js`,
          type: 'product'
        };
      }
      return null;
    };

    const fetchMegaProductRequest = (request) => {
      if (!request) return Promise.resolve([]);
      const cacheKey = `${request.endpoint}:${request.type}`;
      if (!megaProductCache.has(cacheKey)) {
        megaProductCache.set(cacheKey, fetch(request.endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then(async (response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          const data = await response.json();
          if (request.type === 'collection') return data.products || [];
          if (request.type === 'predictive') return data?.resources?.results?.products || [];
          return [data];
        }).catch((error) => {
          megaProductCache.delete(cacheKey);
          throw error;
        }));
      }
      return megaProductCache.get(cacheKey);
    };

    const megaPredictiveSearchRequest = (query, limit) => {
      const normalizedQuery = String(query || '').trim();
      if (!normalizedQuery) return null;
      const params = new URLSearchParams({
        q: normalizedQuery,
        'resources[type]': 'product',
        'resources[limit]': String(limit),
        'resources[options][unavailable_products]': 'show'
      });
      return {
        endpoint: `${window.Shopify?.routes?.root || '/'}search/suggest.json?${params.toString()}`,
        type: 'predictive'
      };
    };

    const fetchMegaProducts = async (rawUrl, limit, fallbackQuery) => {
      const request = megaProductEndpoint(rawUrl, limit);
      let products = await fetchMegaProductRequest(request);
      let resolvedUrl = rawUrl;

      // Shopify redirects legacy collection pages, but the matching
      // /products.json endpoint can still return an empty array. Resolve the
      // storefront redirect and retry the canonical collection before showing
      // an empty state.
      if (!products.length && request?.type === 'collection' && rawUrl) {
        try {
          const response = await fetch(normalizeStorefrontUrl(rawUrl), {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' }
          });
          const canonicalRequest = megaProductEndpoint(response.url, limit);
          if (response.ok && canonicalRequest?.endpoint !== request.endpoint) {
            products = await fetchMegaProductRequest(canonicalRequest);
            if (products.length) resolvedUrl = response.url;
          }
        } catch (error) {
          // Predictive search below keeps the menu useful if the collection
          // redirect cannot be resolved.
        }
      }

      if (!products.length) {
        products = await fetchMegaProductRequest(megaPredictiveSearchRequest(fallbackQuery, limit));
      }

      return { products, resolvedUrl };
    };

    const megaProductImage = (product) => {
      const candidate = product?.featured_image || product?.image || product?.images?.[0];
      const source = typeof candidate === 'string' ? candidate : (candidate?.url || candidate?.src);
      if (!source) return '';
      return source.startsWith('//') ? `https:${source}` : source;
    };

    const optimizeMegaProductImage = (rawUrl) => {
      if (!rawUrl) return '';
      try {
        const url = new URL(rawUrl, window.location.origin);
        if (url.hostname.endsWith('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) {
          url.searchParams.set('width', '480');
        }
        return url.toString();
      } catch (error) {
        return rawUrl;
      }
    };

    const megaProductPrice = (product) => {
      const variant = product?.variants?.find((item) => item.available) || product?.variants?.[0];
      const rawPrice = variant?.price ?? product?.price;
      if (rawPrice === undefined || rawPrice === null || rawPrice === '') return '';
      const numericPrice = Number(rawPrice);
      if (!Number.isFinite(numericPrice)) return '';
      const decimalPrice = typeof rawPrice === 'string' ? numericPrice : numericPrice / 100;
      const currency = window.Shopify?.currency?.active || 'AUD';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(decimalPrice);
    };

    const renderMegaProducts = (mega, products) => {
      const grid = mega.querySelector('[data-rr-mega-product-grid]');
      const status = mega.querySelector('[data-rr-mega-product-status]');
      if (!grid || !status) return;
      grid.replaceChildren();

      products.forEach((product) => {
        const productUrl = product?.url || (product?.handle ? `${window.Shopify?.routes?.root || '/'}products/${product.handle}` : '#');
        const card = document.createElement('a');
        card.className = 'rr-header-mega__product-card';
        card.href = productUrl;

        const media = document.createElement('span');
        media.className = 'rr-header-mega__product-media';
        const imageUrl = megaProductImage(product);
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = optimizeMegaProductImage(imageUrl);
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.fetchPriority = 'low';
          image.width = 320;
          image.height = 240;
          media.append(image);
        } else {
          media.classList.add('is-placeholder');
          media.textContent = '4×4';
        }

        const copy = document.createElement('span');
        copy.className = 'rr-header-mega__product-copy';
        if (product?.vendor) {
          const vendor = document.createElement('small');
          vendor.textContent = product.vendor;
          copy.append(vendor);
        }
        const title = document.createElement('strong');
        title.textContent = product?.title || 'View product';
        copy.append(title);
        const price = megaProductPrice(product);
        if (price) {
          const priceElement = document.createElement('span');
          priceElement.className = 'rr-header-mega__product-price';
          priceElement.textContent = price;
          copy.append(priceElement);
        }

        card.append(media, copy);
        grid.append(card);
      });

      status.hidden = products.length > 0;
      status.textContent = products.length ? '' : (mega.dataset.emptyLabel || 'No products are available in this range yet.');
    };

    const loadMegaProducts = async (mega, rawUrl, title) => {
      if (!mega || !desktopNavigation.matches) return;
      const owningDetails = mega.closest('.rr-header-nav-item--mega');
      if (owningDetails && !owningDetails.open) return;
      const pane = mega.querySelector('[data-rr-mega-products]');
      const heading = mega.querySelector('[data-rr-mega-products-title]');
      const viewLink = mega.querySelector('[data-rr-mega-products-link]');
      const grid = mega.querySelector('[data-rr-mega-product-grid]');
      const status = mega.querySelector('[data-rr-mega-product-status]');
      if (!pane || !heading || !viewLink || !grid || !status) return;

      const displayTitle = mega.classList.contains('rr-header-mega--direct')
        ? 'Available products'
        : (title || 'Matching products');

      if (pane.dataset.currentUrl === rawUrl && grid.childElementCount > 0) {
        heading.textContent = displayTitle;
        viewLink.href = rawUrl || '#';
        return;
      }

      heading.textContent = displayTitle;
      viewLink.href = rawUrl || '#';
      viewLink.hidden = !rawUrl;
      grid.replaceChildren();
      status.hidden = false;
      status.textContent = mega.dataset.loadingLabel || 'Loading products…';
      pane.setAttribute('aria-busy', 'true');
      pane.dataset.currentUrl = rawUrl || '';

      const requestToken = (megaRequestTokens.get(mega) || 0) + 1;
      megaRequestTokens.set(mega, requestToken);
      try {
        const limit = Math.max(2, Math.min(6, Number(mega.dataset.productsLimit) || 6));
        const fallbackQuery = [title, mega.dataset.productsContext].filter(Boolean).join(' ');
        const result = await fetchMegaProducts(rawUrl, limit, fallbackQuery);
        if (megaRequestTokens.get(mega) !== requestToken) return;
        viewLink.href = result.resolvedUrl || rawUrl || '#';
        renderMegaProducts(mega, result.products.slice(0, limit));
      } catch (error) {
        if (megaRequestTokens.get(mega) !== requestToken) return;
        grid.replaceChildren();
        status.hidden = false;
        status.textContent = mega.dataset.errorLabel || 'Open the range to see its products.';
      } finally {
        if (megaRequestTokens.get(mega) === requestToken) pane.setAttribute('aria-busy', 'false');
      }
    };

    const activateMegaMake = (mega, make) => {
      if (!mega || !make) return;
      mega.querySelectorAll('[data-rr-mega-make]').forEach((link) => link.classList.toggle('is-active', link === make));
      loadMegaProducts(mega, make.dataset.rrProductsUrl || make.href, make.dataset.rrProductsTitle || make.textContent.trim());
    };

    const activateMegaCategory = (mega, index = '0') => {
      if (!mega) return;
      mega.classList.add('is-enhanced');
      const key = String(index);
      mega.querySelectorAll('[data-rr-mega-category]').forEach((trigger) => {
        const active = trigger.dataset.rrMegaCategory === key;
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-pressed', String(active));
      });
      let activePanel = null;
      mega.querySelectorAll('[data-rr-mega-panel]').forEach((panel) => {
        const active = panel.dataset.rrMegaPanel === key;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active) activePanel = panel;
      });
      const activeTrigger = mega.querySelector(`[data-rr-mega-category="${key}"]`);
      const firstMake = activePanel?.querySelector('[data-rr-mega-make]');
      if (firstMake) {
        activateMegaMake(mega, firstMake);
      } else if (activeTrigger) {
        mega.querySelectorAll('[data-rr-mega-make]').forEach((link) => link.classList.remove('is-active'));
        loadMegaProducts(mega, activeTrigger.dataset.rrProductsUrl, activeTrigger.dataset.rrProductsTitle || activeTrigger.textContent.trim());
      }
    };

    megaDetails.forEach((details) => {
      const mega = details.querySelector('[data-rr-mega-menu]');
      const triggers = [...details.querySelectorAll('[data-rr-mega-category]')];
      activateMegaCategory(mega, triggers[0]?.dataset.rrMegaCategory || '0');

      triggers.forEach((trigger) => {
        const activate = () => activateMegaCategory(mega, trigger.dataset.rrMegaCategory);
        trigger.addEventListener('pointerenter', activate);
        trigger.addEventListener('focus', activate);
        trigger.addEventListener('click', activate);
        trigger.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = triggers.indexOf(trigger);
          if (event.key === 'ArrowDown') nextIndex = (nextIndex + 1) % triggers.length;
          if (event.key === 'ArrowUp') nextIndex = (nextIndex - 1 + triggers.length) % triggers.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = triggers.length - 1;
          triggers[nextIndex]?.focus();
        });
      });

      details.querySelectorAll('[data-rr-mega-make]').forEach((make) => {
        const activate = () => activateMegaMake(mega, make);
        make.addEventListener('pointerenter', activate);
        make.addEventListener('focus', activate);
      });

      details.addEventListener('toggle', () => {
        if (!details.open || !desktopNavigation.matches) return;
        clearDesktopOpenTimer();
        clearDesktopCloseTimer();
        desktopDetails.forEach((sibling) => {
          if (sibling !== details) closeDesktopDetails(sibling);
        });
        const activeTrigger = mega.querySelector('[data-rr-mega-category].is-active') || triggers[0];
        activateMegaCategory(mega, activeTrigger?.dataset.rrMegaCategory || '0');
      });
    });

    moreDetails.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open || !desktopNavigation.matches) return;
        clearDesktopOpenTimer();
        clearDesktopCloseTimer();
        desktopDetails.forEach((sibling) => {
          if (sibling !== details) closeDesktopDetails(sibling);
        });
      });
    });

    menuButton?.addEventListener('click', () => {
      syncMobileHeaderOffset();
      const willOpen = menuButton.getAttribute('aria-expanded') !== 'true';
      if (willOpen) closeSearch(header);
      menuButton.setAttribute('aria-expanded', String(willOpen));
      menuButton.setAttribute('aria-label', willOpen ? (header.dataset.menuCloseLabel || 'Close menu') : (header.dataset.menuOpenLabel || 'Open menu'));
      if (menu) menu.hidden = !willOpen;
      document.body.classList.toggle('rr-menu-open', willOpen);
      if (willOpen) menu?.querySelector('a, button')?.focus();
    });

    searchButton?.addEventListener('click', () => {
      syncMobileHeaderOffset();
      const willOpen = !search?.classList.contains('is-mobile-open');
      if (willOpen) closeMenu(header);
      search?.classList.toggle('is-mobile-open', willOpen);
      searchButton.setAttribute('aria-expanded', String(willOpen));
      searchButton.setAttribute('aria-label', willOpen ? (header.dataset.searchCloseLabel || 'Close product search') : (header.dataset.searchOpenLabel || 'Open product search'));
      if (willOpen) window.setTimeout(() => searchInput?.focus(), 30);
    });

    header.addEventListener('click', (event) => {
      if (event.target.closest('[data-rr-mobile-menu] a, [data-rr-mobile-menu] button')) closeMenu(header);
      if (event.target.matches?.('[data-rr-mobile-menu]')) closeMenu(header, true);
      if (desktopNavigation.matches && event.target.closest('a[href]')) closeAllDesktopDetails();
    });

    header.addEventListener('keydown', (event) => {
      keyboardNavigation = true;
      const menuIsOpen = menuButton?.getAttribute('aria-expanded') === 'true' && menu && !menu.hidden;
      if (event.key === 'Escape') {
        if (search?.classList.contains('is-mobile-open')) closeSearch(header, true);
        else if (desktopNavigation.matches) {
          const openMega = desktopDetails.find((details) => details.open);
          if (openMega) {
            closeAllDesktopDetails();
            openMega.querySelector(':scope > summary')?.focus();
            event.preventDefault();
          }
        } else closeMenu(header, true);
        return;
      }

      if (event.key === 'Tab' && menuIsOpen) {
        const focusable = [...menu.querySelectorAll(FOCUSABLE)].filter((element) => element.getClientRects().length > 0);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    document.addEventListener('pointerdown', (event) => {
      keyboardNavigation = false;
      if (!desktopNavigation.matches) return;
      const target = event.target instanceof Node ? event.target : null;
      const openDetails = desktopDetails.find((details) => details.open);
      if (!openDetails && desktopOpenTimer === null) return;
      if (openDetails && target && openDetails.contains(target)) return;
      closeAllDesktopDetails();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !desktopNavigation.matches) return;
      const openDetails = desktopDetails.find((details) => details.open);
      if (!openDetails) return;
      const focusWasInside = openDetails.contains(document.activeElement);
      closeAllDesktopDetails();
      if (focusWasInside) openDetails.querySelector(':scope > summary')?.focus();
      event.preventDefault();
    });

    desktopNavigation.addEventListener('change', (event) => {
      closeAllDesktopDetails();
      if (event.matches) {
        closeMenu(header);
        closeSearch(header);
      }
    });

    window.addEventListener('pagehide', closeAllDesktopDetails);
    window.addEventListener('popstate', closeAllDesktopDetails);
    window.addEventListener('hashchange', closeAllDesktopDetails);

    syncMobileHeaderOffset();
    if ('ResizeObserver' in window) {
      const headerObserver = new ResizeObserver(syncMobileHeaderOffset);
      headerObserver.observe(header);
      const announcement = document.querySelector('.rr-announcement');
      if (announcement) headerObserver.observe(announcement);
    }
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-rr-site-header]').forEach(initializeHeader);
  };

  const closeHeaderOverlays = () => {
    document.querySelectorAll('[data-rr-site-header]').forEach((header) => {
      closeMenu(header);
      closeSearch(header);
      header.__rrCloseDesktopNavigation?.();
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest('[data-rr-cart-open], [data-rr-garage-open], [data-rr-enquiry-open]')) closeHeaderOverlays();
  }, true);
  document.addEventListener('rr:cart:open-state', closeHeaderOverlays);

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* Keep collection facets convenient on desktop without forcing a long open filter stack on mobile. */
(() => {
  const desktop = window.matchMedia('(min-width: 990px)');
  const initializeFacets = (facets) => {
    if (!facets || facets.dataset.rrFacetsInitialized === 'true') return;
    facets.dataset.rrFacetsInitialized = 'true';
    const sync = () => { facets.open = desktop.matches && facets.dataset.openDesktop === 'true'; };
    sync();
    desktop.addEventListener('change', sync);
  };
  const initialize = (root = document) => root.querySelectorAll('[data-rr-catalog-facets]').forEach(initializeFacets);
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* Predictive search progressively enhances the native Shopify search form. */
(() => {
  const escapeText = (value) => String(value ?? '');
  const formatSearchPrice = (value) => {
    const amount = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(amount)) return escapeText(value);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'AUD',
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };
  const usefulVendor = (vendor) => {
    const value = escapeText(vendor).trim();
    return /^(road\s*rex\s*4x4|roadrex4x4)$/i.test(value) ? '' : value;
  };
  const createProductResult = (product, index) => {
    const link = document.createElement('a');
    link.className = 'rr-search-result';
    link.href = product.url;
    link.id = `RRSearchOption-product-${index}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');

    if (product.featured_image?.url) {
      const image = document.createElement('img');
      image.src = `${product.featured_image.url}${product.featured_image.url.includes('?') ? '&' : '?'}width=160`;
      image.alt = product.featured_image.alt || product.title || '';
      image.width = 80;
      image.height = 80;
      image.loading = 'lazy';
      link.append(image);
    }

    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = escapeText(product.title);
    const meta = document.createElement('small');
    meta.textContent = [usefulVendor(product.vendor), product.price ? formatSearchPrice(product.price) : ''].filter(Boolean).join(' · ');
    copy.append(title, meta);
    link.append(copy);
    return link;
  };

  const createArticleResult = (article, index) => {
    const link = document.createElement('a');
    link.className = 'rr-search-article';
    link.href = article.url;
    link.id = `RRSearchOption-article-${index}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    const eyebrow = document.createElement('small');
    eyebrow.textContent = 'Advice';
    const title = document.createElement('strong');
    title.textContent = escapeText(article.title);
    link.append(eyebrow, title);
    return link;
  };

  const initializeSearch = (search) => {
    if (!search || search.dataset.rrSearchInitialized === 'true') return;
    search.dataset.rrSearchInitialized = 'true';
    const form = search.querySelector('form');
    const input = search.querySelector('input[type="search"]');
    const productsRoot = search.querySelector('[data-predictive-search]');
    const articlesRoot = search.querySelector('[data-predictive-search-article]');
    const status = search.querySelector('.predictive-search-status');
    const endpoint = search.dataset.predictiveUrl;
    if (!form || !input || !productsRoot || !endpoint) return;

    let timer;
    let controller;
    const options = () => [...search.querySelectorAll('[role="option"]')];
    const clearActiveOption = () => {
      options().forEach((option) => option.setAttribute('aria-selected', 'false'));
      input.removeAttribute('aria-activedescendant');
    };
    const close = () => {
      search.removeAttribute('open');
      input.setAttribute('aria-expanded', 'false');
      clearActiveOption();
    };

    const run = async () => {
      const term = input.value.trim();
      controller?.abort();
      if (term.length < 2) {
        productsRoot.replaceChildren();
        articlesRoot?.replaceChildren();
        close();
        return;
      }

      controller = new AbortController();
      status.textContent = search.dataset.loadingText || 'Searching products';
      try {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': articlesRoot ? 'product,article' : 'product',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last'
        });
        const response = await fetch(`${endpoint}?${params}`, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const payload = await response.json();
        const results = payload.resources?.results || {};
        const products = results.products || [];
        const articles = results.articles || [];

        productsRoot.replaceChildren();
        articlesRoot?.replaceChildren();
        products.forEach((product, index) => productsRoot.append(createProductResult(product, index)));
        articles.forEach((article, index) => articlesRoot?.append(createArticleResult(article, index)));

        if (!products.length && !articles.length) {
          const empty = document.createElement('p');
          empty.className = 'rr-search-empty';
          empty.textContent = `No quick matches for “${term}”. Press Enter to search the full catalogue.`;
          productsRoot.append(empty);
        }

        search.setAttribute('open', '');
        input.setAttribute('aria-expanded', 'true');
        status.textContent = `${products.length + articles.length} suggestions available`;
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 220);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowDown' && search.hasAttribute('open')) {
        const first = options()[0];
        if (first) {
          event.preventDefault();
          first.focus();
          first.setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', first.id);
        }
      }
    });
    search.addEventListener('keydown', (event) => {
      const current = event.target.closest?.('[role="option"]');
      if (!current) return;
      const resultOptions = options();
      const currentIndex = resultOptions.indexOf(current);
      let next;
      if (event.key === 'ArrowDown') next = resultOptions[currentIndex + 1] || resultOptions[0];
      if (event.key === 'ArrowUp') next = resultOptions[currentIndex - 1] || resultOptions[resultOptions.length - 1];
      if (next) {
        event.preventDefault();
        resultOptions.forEach((option) => option.setAttribute('aria-selected', String(option === next)));
        input.setAttribute('aria-activedescendant', next.id);
        next.focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close();
        input.focus();
      }
    });
    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) close();
    });
  };

  const initialize = (root = document) => root.querySelectorAll('[data-rr-predictive-search]').forEach(initializeSearch);
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* Native Road Rex 4x4 collection controls. */
(() => {
  const initializeCollection = (collection) => {
    if (!collection || collection.dataset.rrCollectionA11y === 'true') return;
    collection.dataset.rrCollectionA11y = 'true';

    const opener = collection.querySelector('.collection-filter__button');
    const panel = collection.querySelector('[id^="RRCollectionFilters-"]') || document.querySelector('[id^="RRCollectionFilters-"]');

    if (opener && panel && !opener.getAttribute('aria-controls')) opener.setAttribute('aria-controls', panel.id);

    const sync = () => {
      const isOpen = document.body.classList.contains('collection-sidebar--open');
      if (opener) opener.setAttribute('aria-expanded', String(isOpen));
    };

    collection.addEventListener('click', (event) => {
      if (!event.target.closest('.js-sidebar-toggle, .collection-sidebar__overlay')) return;
      window.setTimeout(sync, 0);
      if (event.target.closest('.collection-sidebar__close')) window.setTimeout(() => opener?.focus(), 0);
    });

    collection.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !document.body.classList.contains('collection-sidebar--open')) return;
      document.body.classList.remove('collection-sidebar--open');
      sync();
      opener?.focus();
    });

    sync();
  };

  const initialize = (root = document) => {
    root.querySelectorAll('.js-page-collection').forEach(initializeCollection);
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* A lightweight, theme-owned wishlist state for product cards and the PDP. */
(() => {
  const key = 'roadrex-wishlist-v1';
  let fallbackItems = [];
  let storageAvailable = true;
  const read = () => {
    if (!storageAvailable) return fallbackItems;
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      fallbackItems = Array.isArray(value) ? value : [];
      return fallbackItems;
    } catch (_error) {
      storageAvailable = false;
      return fallbackItems;
    }
  };
  const write = (items) => {
    fallbackItems = [...items];
    if (!storageAvailable) return;
    try { window.localStorage.setItem(key, JSON.stringify(items)); } catch (_error) { storageAvailable = false; }
  };
  const sync = (root = document) => {
    const saved = new Set(read());
    root.querySelectorAll('[data-rr-wishlist][data-product-handle]').forEach((button) => {
      const active = saved.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('is-saved', active);
    });
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-rr-wishlist][data-product-handle]');
    if (!button) return;
    event.preventDefault();
    const items = new Set(read());
    if (items.has(button.dataset.productHandle)) items.delete(button.dataset.productHandle);
    else items.add(button.dataset.productHandle);
    write([...items]);
    sync();
  });
  document.addEventListener('DOMContentLoaded', () => sync());
  document.addEventListener('rr:cards:ready', (event) => sync(event.detail?.root || document));
  document.addEventListener('shopify:section:load', (event) => sync(event.target));
})();

}
